<script setup lang="ts" generic="T extends string | number">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Avatar from '@/components/common/ui/Avatar.vue'
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline'

type Option = { id: T; text: string; subText?: string }

const props = withDefaults(
  defineProps<{
    options: Option[]
    /** Singular noun, used for empty/single labels (e.g. "commercial", "utilisateur") */
    unit?: string
    /** Plural noun, used for "Tous les ..." and counter labels */
    unitPlural?: string
    /** When true, hides the avatar column (useful if subText isn't an email) */
    hideAvatar?: boolean
  }>(),
  {
    unit: 'élément',
    unitPlural: 'éléments',
    hideAvatar: false,
  },
)

const modelValue = defineModel<T[]>({ default: () => [] })

const isOpen = ref(false)
const search = ref('')
const rootRef = ref<HTMLElement | null>(null)

const sortedOptions = computed(() =>
  [...props.options].sort((a, b) =>
    a.text.localeCompare(b.text, 'fr', { sensitivity: 'base' }),
  ),
)

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()
  if (!term) return sortedOptions.value
  return sortedOptions.value.filter(
    (o) =>
      o.text.toLowerCase().includes(term) ||
      (o.subText ?? '').toLowerCase().includes(term),
  )
})

const selected = computed<T[]>(() => modelValue.value ?? [])
const selectedOptions = computed(() =>
  sortedOptions.value.filter((o) => selected.value.includes(o.id)),
)
const allSelected = computed(
  () =>
    sortedOptions.value.length > 0 &&
    sortedOptions.value.every((o) => selected.value.includes(o.id)),
)
const someSelected = computed(
  () => selected.value.length > 0 && !allSelected.value,
)

const MAX_AVATARS_IN_TRIGGER = 4
const visibleAvatars = computed(() =>
  selectedOptions.value.slice(0, MAX_AVATARS_IN_TRIGGER),
)
const hiddenAvatarsCount = computed(() =>
  Math.max(0, selectedOptions.value.length - visibleAvatars.value.length),
)

const summaryLabel = computed(() => {
  const count = selected.value.length
  if (count === 0) return `Sélectionner les ${props.unitPlural}`
  if (allSelected.value) return `Tous les ${props.unitPlural} (${count})`
  if (count === 1) {
    return selectedOptions.value[0]?.text ?? `1 ${props.unit}`
  }
  if (hiddenAvatarsCount.value > 0) {
    return `${count} ${props.unitPlural} (+${hiddenAvatarsCount.value})`
  }
  return `${count} ${props.unitPlural} sélectionnés`
})

function isChecked(id: T) {
  return selected.value.includes(id)
}

function toggle(id: T) {
  if (isChecked(id)) {
    modelValue.value = selected.value.filter((v) => v !== id)
  } else {
    modelValue.value = [...selected.value, id]
  }
}

function toggleAll() {
  modelValue.value = allSelected.value
    ? []
    : sortedOptions.value.map((o) => o.id)
}

function clearSelection() {
  modelValue.value = []
}

function handleClickOutside(event: MouseEvent) {
  if (rootRef.value && !rootRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') isOpen.value = false
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <div ref="rootRef" class="relative w-full">
    <button
      type="button"
      class="btn btn-sm btn-outline w-full justify-between border-base-300 bg-white font-normal hover:bg-base-100"
      :class="{ 'ring-2 ring-primary ring-offset-1': isOpen }"
      @click="isOpen = !isOpen"
    >
      <span class="flex items-center gap-2 min-w-0">
        <UsersIcon
          v-if="hideAvatar || selected.length === 0 || allSelected"
          class="h-4 w-4 shrink-0 text-gray-500"
        />
        <div v-else class="flex shrink-0 -space-x-2">
          <div
            v-for="o in visibleAvatars"
            :key="String(o.id)"
            class="avatar"
          >
            <div class="h-6 w-6 rounded-full ring-2 ring-white overflow-hidden">
              <Avatar :email="o.subText" size="48" />
            </div>
          </div>
        </div>
        <span class="truncate">{{ summaryLabel }}</span>
      </span>
      <span class="flex items-center gap-1 shrink-0">
        <span v-if="selected.length > 0" class="badge badge-primary badge-sm">
          {{ selected.length }}
        </span>
        <span
          v-if="selected.length > 0"
          role="button"
          tabindex="0"
          aria-label="Tout retirer"
          class="tooltip flex items-center justify-center rounded-full p-0.5 hover:bg-base-200"
          data-tip="Tout retirer"
          @click.stop="clearSelection"
          @keydown.enter.stop.prevent="clearSelection"
          @keydown.space.stop.prevent="clearSelection"
        >
          <XMarkIcon class="h-4 w-4" />
        </span>
        <ChevronDownIcon
          class="h-4 w-4 text-gray-500 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </span>
    </button>

    <div
      v-if="isOpen"
      class="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border border-base-300 bg-base-100 shadow-lg"
    >
      <div class="border-b border-base-200 p-2">
        <label class="input input-sm input-bordered flex items-center gap-2">
          <MagnifyingGlassIcon class="h-4 w-4 opacity-60" />
          <input
            v-model="search"
            type="text"
            class="grow"
            :placeholder="`Rechercher un ${unit}...`"
          />
        </label>
      </div>

      <label
        v-if="sortedOptions.length > 0"
        class="flex cursor-pointer items-center gap-3 border-b border-base-200 bg-base-50 px-3 py-2 hover:bg-base-200"
      >
        <input
          type="checkbox"
          class="checkbox checkbox-sm checkbox-primary"
          :checked="allSelected"
          :indeterminate.prop="someSelected"
          @change="toggleAll"
        />
        <span class="font-semibold">
          {{ allSelected ? 'Tout désélectionner' : 'Tout sélectionner' }}
        </span>
        <span class="ml-auto text-xs text-gray-500">
          {{ selected.length }} / {{ sortedOptions.length }}
        </span>
      </label>

      <ul class="max-h-72 overflow-y-auto py-1">
        <li v-for="o in filtered" :key="String(o.id)">
          <label
            class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-base-200"
          >
            <input
              type="checkbox"
              class="checkbox checkbox-sm checkbox-primary"
              :checked="isChecked(o.id)"
              @change="toggle(o.id)"
            />
            <div v-if="!hideAvatar" class="avatar">
              <div class="w-8 h-8 rounded-full overflow-hidden">
                <Avatar :email="o.subText" size="48" />
              </div>
            </div>
            <div class="min-w-0 flex-1">
              <p class="truncate font-medium">{{ o.text }}</p>
              <p v-if="o.subText" class="truncate text-xs text-gray-500">
                {{ o.subText }}
              </p>
            </div>
          </label>
        </li>
        <li
          v-if="filtered.length === 0"
          class="px-3 py-4 text-center text-sm text-gray-500"
        >
          Aucun {{ unit }} trouvé
        </li>
      </ul>
    </div>
  </div>
</template>
